import { useIsDropboxer } from "@mirage/service-auth/useDropboxAccount";
import { getAll, listen, set } from "@mirage/service-settings";
import { useConsolidatedSubscription } from "@mirage/shared/hooks/useConsolidatedSubscription";
import { useStableObject } from "@mirage/shared/util/hooks";
import { atom, useAtom } from "jotai";
import { useCallback, useMemo } from "react";

import type { SettingsPartial } from "@mirage/service-settings";

import { AnnotationMode, type Settings } from "./service/types";

export const DefaultKeys: (keyof Settings)[] = [
  "annotationMode",
  "appDebug",
  "appShortcut",
  "persistentAppShortcut",
  "appToNearestWindow",
  "collapsedCards",
  "customizableModules",
  "disableAutoUpdate",
  "enableDarkMode",
  "enableTips",
  "hideAppOnStartup",
  "isDevTools",
  "localFiles",
  "showDashInDock",
  "openFilesInDesktopAppsV2",
  "stackByIdSortPreference",
  "stackItemByIdCollapseDescription",
  "stackItemByIdCollapseSummary",
  "stackPageFilterPreference",
  "stackPageSortPreference",
  "dismissedTooltips",
  "disableGlobalShortcut",
  "startPageSelectedTab",
  "startPageSelectedTabExperimentDone",
  "dismissedMobileAppDownloadBanner",
  "locale",
  "appShortcuts",
  "region",
];

const settingsAtom = atom<Settings | undefined>(undefined);

export function useAllSettings() {
  const [settings, setSettings] = useAtom(settingsAtom);

  const subscribe = useCallback(
    (update) => listen(DefaultKeys).subscribe(update),
    [],
  );

  useConsolidatedSubscription({
    hookName: "useAllSettings",
    setState: setSettings,
    getInitialData: getAll,
    subscribe,
  });

  return settings;
}

export default function useSettings<K extends keyof Settings>(
  keys: K[] | Readonly<K[]>,
) {
  const allSettings = useAllSettings();
  const stableKeys = useStableObject(keys);

  const settings = useMemo(() => {
    if (!allSettings) return null;

    const newSettings = {} as Settings;
    for (const key of stableKeys) {
      newSettings[key] = allSettings[key];
    }
    return newSettings as SettingsPartial<K>;
  }, [allSettings, stableKeys]);

  return { settings, set };
}

export function useAnnotationMode(
  _useSettings: typeof useSettings = useSettings,
) {
  const isDropboxer = useIsDropboxer();
  const { settings, set } = _useSettings(["annotationMode"]);

  const isAnnotationModeOn = useMemo(() => {
    if (
      isDropboxer &&
      settings?.annotationMode !== AnnotationMode.OFF_BY_DROPBOXER
    ) {
      return true;
    }
    return settings?.annotationMode === AnnotationMode.ON;
  }, [isDropboxer, settings?.annotationMode]);

  return {
    isAnnotationModeOn,
    toggleAnnotationMode: () => {
      const currentValue = settings?.annotationMode;
      if (isDropboxer) {
        if (currentValue === AnnotationMode.ON) {
          set("annotationMode", AnnotationMode.OFF_BY_DROPBOXER);
        } else {
          set("annotationMode", AnnotationMode.ON);
        }
      } else {
        set(
          "annotationMode",
          currentValue == AnnotationMode.OFF
            ? AnnotationMode.ON
            : AnnotationMode.OFF,
        );
      }
    },
  };
}
